import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchStudentPaperAssignmentsAPI,
  submitStudentSolutionAPI,
  downloadFeedbackFileAPI,
  downloadOriginalAssignmentAPI,
  downloadMySubmissionAPI
} from "./paperAssignmentAPI";

// Async thunks
export const fetchStudentPaperAssignments = createAsyncThunk(
  "paperAssignment/fetchStudentPaperAssignments",
  async (studentId, { rejectWithValue }) => {
    try {
      const response = await fetchStudentPaperAssignmentsAPI(studentId);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const submitStudentSolution = createAsyncThunk(
  "paperAssignment/submitStudentSolution",
  async ({ studentId, formData }, { rejectWithValue }) => {
    try {
      const response = await submitStudentSolutionAPI(studentId, formData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const downloadFeedbackFile = createAsyncThunk(
  "paperAssignment/downloadFeedbackFile",
  async ({ studentId, s3Key }, { rejectWithValue }) => {
    try {
      const response = await downloadFeedbackFileAPI(studentId, s3Key);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const downloadOriginalAssignment = createAsyncThunk(
  "paperAssignment/downloadOriginalAssignment",
  async ({ studentId, s3Key }, { rejectWithValue }) => {
    try {
      const response = await downloadOriginalAssignmentAPI(studentId, s3Key);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const downloadMySubmission = createAsyncThunk(
  "paperAssignment/downloadMySubmission",
  async ({ studentId, s3Key }, { rejectWithValue }) => {
    try {
      const response = await downloadMySubmissionAPI(studentId, s3Key);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Initial state
const initialState = {
  assignments: [],
  loading: false,
  uploadingHomeworkId: null,
  error: null,
};

// Slice
const paperAssignmentSlice = createSlice({
  name: "paperAssignment",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setUploadingHomeworkId: (state, action) => {
      state.uploadingHomeworkId = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Student Paper Assignments
      .addCase(fetchStudentPaperAssignments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStudentPaperAssignments.fulfilled, (state, action) => {
        state.loading = false;
        state.assignments = action.payload;
      })
      .addCase(fetchStudentPaperAssignments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Submit Student Solution
      .addCase(submitStudentSolution.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(submitStudentSolution.fulfilled, (state, action) => {
        state.loading = false;
        state.uploadingHomeworkId = null;
        // Update the assignments state to reflect the submission
        // This will be handled by re-fetching the assignments
      })
      .addCase(submitStudentSolution.rejected, (state, action) => {
        state.loading = false;
        state.uploadingHomeworkId = null;
        state.error = action.payload;
      })
      
      // Download Feedback File
      .addCase(downloadFeedbackFile.pending, (state) => {
        state.error = null;
      })
      .addCase(downloadFeedbackFile.fulfilled, (state, action) => {
        // File download is handled in component
      })
      .addCase(downloadFeedbackFile.rejected, (state, action) => {
        state.error = action.payload;
      });
  },
});

export const { clearError, setUploadingHomeworkId } = paperAssignmentSlice.actions;

export default paperAssignmentSlice.reducer;